import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import hansPhoto from "@/assets/hans-photo.webp";
import shawnPhoto from "@/assets/shawn-photo.webp";
import teamOfficeFull from "@/assets/team-office-full.webp";
import eventGroup from "@/assets/team-event-group.webp";
import eventDuo from "@/assets/team-event-duo.webp";
import eventThree from "@/assets/team-event-3.webp";

const founders = [
  {
    name: "Hans Hillestad",
    role: "CEO & Co-Founder",
    image: hansPhoto,
    bio: "Hans co-founded Xpress Inc in 2005, starting as a direct mail operation and growing it into one of the most trusted dental marketing agencies in the country. With his own family dental practice scaling from $1M to $12M in revenue, Hans has lived the growth journey firsthand — testing every strategy before recommending it to clients. Two decades of hands-on experience means no guesswork, just what works.",
  },
  {
    name: "Shawn Kimmel",
    role: "COO & Co-Founder",
    image: shawnPhoto,
    bio: "Shawn has been Hans's partner since day one, building Xpress Inc from a small startup into a full-service dental marketing powerhouse. With 20 years of deep expertise in the dental space, Shawn oversees operations and client strategy — ensuring every practice gets the same battle-tested playbook that took their own family practice from a single location to a multi-million dollar operation.",
  },
];

const eventPhotos = [
  { src: eventGroup, alt: "XPress team meeting with dental practice clients at an industry event" },
  { src: eventDuo, alt: "Hans and client at a dental marketing conference" },
  { src: eventThree, alt: "XPress Promotion team connecting with partners" },
];

const TeamSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-background" ref={ref}>
      <div className="max-w-7xl mx-auto section-padding">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-accent font-semibold text-sm tracking-wide uppercase mb-3">
            Real Team, Real Results
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
            Meet the People Behind Your Growth
          </h2>
        </div>

        {/* Full team office photo */}
        <div className={`mb-14 ${visible ? "reveal-up" : "opacity-0"}`}>
          <div className="rounded-xl overflow-hidden shadow-lg aspect-[21/9]">
            <img
              src={teamOfficeFull}
              alt="The full XPress Promotion team at their Springfield, VA office"
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
          </div>
          <p className="text-center text-sm text-muted-foreground mt-3">
            <a
              href="https://www.xpresspromotion.com/about"
              className="hover:text-accent transition-colors duration-200 underline underline-offset-2"
            >
              Our in-house team at XPress HQ
            </a>{" "}
            — Springfield, VA
          </p>
        </div>

        {/* Founders */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {founders.map((f, i) => (
            <div
              key={f.name}
              className={`flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-card border border-border rounded-xl p-8 shadow-sm ${
                visible ? "reveal-up" : "opacity-0"
              }`}
              style={{ animationDelay: `${i * 120 + 150}ms` }}
            >
              <img
                src={f.image}
                alt={f.name}
                className="w-32 h-40 rounded-lg object-cover object-top shadow-md flex-shrink-0"
                loading="lazy"
              />
              <div className="text-center sm:text-left">
                <h3 className="text-xl font-bold text-foreground">{f.name}</h3>
                <p className="text-sm font-semibold text-accent mb-3">{f.role}</p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">{f.bio}</p>
                <a
                  href="https://www.xpresspromotion.com/about"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:gap-3 transition-all duration-200"
                >
                  Hear Their Story
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Event photos */}
        <div className={`${visible ? "reveal-up" : "opacity-0"}`} style={{ animationDelay: "400ms" }}>
          <p className="text-center text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-6">
            Connecting with Clients at Industry-Leading Dental Events
          </p>
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {eventPhotos.map((photo, i) => (
              <div key={i} className="rounded-lg overflow-hidden aspect-[4/3] shadow-sm">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
